import { useState } from 'react';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from "@mui/material/Select";
import useMediaQuery from '@mui/material/useMediaQuery';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

interface DashboardLoaderProps {
  publishStartDate: (value: Date) => void;
  publishEndDate: (value: Date) => void;
}

const DashboardLoader = ({ publishStartDate, publishEndDate }: DashboardLoaderProps) => {
  const isNonMobile = useMediaQuery("(min-width:600px)");

  // Default: Load the dashboard data for the last "Two Weeks".
  const twoWeeksAgo = new Date();
  twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
  const [range, setRange] = useState("W2");
  const [startDate, setStartDate] = useState(twoWeeksAgo);
  const [endDate, setEndDate] = useState(new Date());

  const rangeOptions = [
    { value: 'D0', label: 'Today' },
    { value: 'W1', label: 'One Week' },
    { value: 'W2', label: 'Two Weeks' },
    { value: 'W3', label: 'Three Weeks' },
    { value: 'W4', label: 'Four Weeks' },
    { value: 'M1', label: 'One Month' },
    { value: 'M2', label: 'Two Months' },
    { value: 'M3', label: 'Three Months' },
    { value: 'M4', label: 'Four Months' },
    { value: 'M5', label: 'Five Months' },
    { value: 'M6', label: 'Six Months' },
    { value: 'Y0', label: 'Year to Date' },
    { value: 'Y1', label: 'One Year' }
  ];

  const handleRangeChange = (event: SelectChangeEvent) => { 
    setRange(event.target.value);

    const datePart = event.target.value.charAt(0);
    const dateShift = parseInt(event.target.value.charAt(1));
    const newStartDate = new Date();

    switch (datePart) {
      case 'D':
      case 'W':
        newStartDate.setDate(newStartDate.getDate() - 7 * dateShift);
        break;
      case 'M':
        newStartDate.setMonth(newStartDate.getMonth() - dateShift);
        break;
      case 'Y':
        if (dateShift === 0) {
          newStartDate.setDate(1);
          newStartDate.setMonth(0);
        } else {
          newStartDate.setFullYear(newStartDate.getFullYear() - dateShift);
        }
        break;
    }

    setStartDate(newStartDate);
    setEndDate(new Date());
    publishStartDate(newStartDate);
    publishEndDate(new Date());
  };

  return (
    <Box 
      display="grid" 
      gridTemplateColumns="repeat(12, minmax(0, 1fr))"
      gap="12px"
      sx={{
        "& > div": { gridColumn: isNonMobile ? undefined : "span 12" }
      }} 
    >
      <Select
        fullWidth
        color="info"
        value={range}
        onChange={handleRangeChange}
        sx={{ gridColumn: "span 4" }}
      >
        {rangeOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker 
          label="Start Date" 
          value={dayjs(startDate)} 
          onChange={(value) => {
            setStartDate(value?.toDate() ?? new Date());
            publishStartDate(value?.toDate() ?? new Date());
          }}
          slotProps={{ 
            textField: { 
              fullWidth: true,
              color: "info"
            }
          }}
          sx={{ gridColumn: "span 4" }}
        />
      </LocalizationProvider>

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label="End Date"
          value={dayjs(endDate)}
          onChange={(value) => {
            setEndDate(value?.toDate() ?? new Date());
            publishEndDate(value?.toDate() ?? new Date());
          }}
          slotProps={{ 
            textField: { 
              fullWidth: true,
              color: "info"
            }
          }}
          sx={{ gridColumn: "span 4" }}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default DashboardLoader;